'use client'

import { useEffect } from 'react'
import { useEventEmitterContext } from '@/lib/contexts/EventEmitterContext'
import { useSearchStore } from '@/lib/store/search-store'
import { useParams, usePathname, useRouter } from 'next/navigation'

export default function SearchStoreRouter() {
  const appEvent$ = useEventEmitterContext()
  const search = useSearchStore((state) => state.search)
  const setSearch = useSearchStore((state) => state.setSearch)
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)
  const setSearchMainCategorySlug = useSearchStore((state) => state.setSearchMainCategorySlug)
  const router = useRouter()
  const pathname = usePathname()
  const isSearchPage = pathname.startsWith('/search')

  useEffect(() => {
    console.debug('SearchStoreRouter mounted')
  }, [])

  // Subscribe to the app event emitter for search callbacks
  appEvent$.useSubscription((evt) => {
    if (evt.type === 'search:callback') {
      const { query, mainCategory } = evt.payload
      let searchStateUpdated = false
      console.debug('search:callback event received', { query, mainCategory })

      if (query !== (search ?? '')) {
        console.debug('Updating search query in store', search, '>', query)
        setSearch(query)
        searchStateUpdated = true
      }

      const resolvedMainCategory = mainCategory === 'all' ? undefined : mainCategory
      if (searchMainCategorySlug !== resolvedMainCategory) {
        console.debug('Updating search main category in store', searchMainCategorySlug, '>', resolvedMainCategory)
        setSearchMainCategorySlug(resolvedMainCategory)
        searchStateUpdated = true
      }

      if (!searchStateUpdated) {
        console.debug('No changes in search state, skipping navigation')
        return
      }

      const newPathname = `/search/${resolvedMainCategory ?? 'all'}/${encodeURIComponent(query ?? '')}`

      router.push(newPathname)
    }
  })

  // Also when url changes, update the search state
  const { params } = useParams()
  useEffect(() => {
    if (isSearchPage) {
      const [mainCategorySlug, query] = params ?? []
      setSearch(query ? decodeURIComponent(query) : undefined)
      setSearchMainCategorySlug(mainCategorySlug === 'all' ? undefined : mainCategorySlug)
    }
  }, [isSearchPage, params, setSearch, setSearchMainCategorySlug])

  // Clear the search state when navigating away from the search page
  useEffect(() => {
    if (!isSearchPage) {
      console.debug('Not on search page, clearing search state')
      setSearch(undefined)
      setSearchMainCategorySlug(undefined)
    }
  }, [isSearchPage, setSearch, setSearchMainCategorySlug])

  return null
}
